import {
  DataResourceDetail,
  GeneralResourceDetail,
  MpxItemResponse,
  ResourceDetailsUpdate,
  TraitId,
} from '@cibo/core'
import { FieldNameCell } from '@cibo/landmanager'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import {
  Box,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { flatten, uniq } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementDetails, useProgramEngagementFields } from '../../../../queries'
import { MultiselectChangeAllInput, MultiselectFieldInput } from './MultiselectFieldInput'

interface FieldMultiSelectProps {
  sources: TraitId[] | string[]
  optionsList?: { value: string; label: string }[]
  traitId: TraitId
  handleChange: (
    updates: ResourceDetailsUpdate[]
  ) => Promise<MpxItemResponse<GeneralResourceDetail>>
  dataEntryFieldDetails?: {
    resourceId: string
    details: DataResourceDetail[]
  }[]
  disabled?: boolean
}

export const MultiselectCheckboxFieldTable = ({
  detailRequirements,
  engagementId,
  onError,
  dataEntryFieldDetails,
  onSuccess,
  onUpdating,
  sources,
  optionsList,
  traitId,
  handleChange,
}: Omit<
  ResourceDetailFeatureTaskEditorProps,
  'ownerName' | 'userRole' | 'resourceIds' | 'engagement'
> &
  FieldMultiSelectProps) => {
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')
  const programEngagementFields = useProgramEngagementFields({ id: engagementId })
  const engagementDetails = useProgramEngagementDetails({ resourceId: engagementId })
  const { enqueueSnackbar } = useSnackbar()
  const year = detailRequirements?.[0]?.year
  const [combinedAnswer, setCombinedAnswer] = useState<Record<string, string>>({})
  const [fieldAnswers, setFieldAnswers] = useState<Record<string, string[]>>({})
  const [allDisabled, setDisableAll] = useState<boolean>(false)

  const options = useMemo(
    () =>
      !!optionsList
        ? (flatten(
            engagementDetails.data
              ?.map(({ result }) => sources.map(source => (result as Record<string, any>)[source]))
              .map(item => item[0]?.value) ?? []
          ).filter(Boolean) as string[])
        : (flatten(
            engagementDetails.data
              ?.filter(({ traitId }) => sources.includes(traitId))
              .map(({ result }) => result) ?? []
          ) as string[]),
    [engagementDetails.dataUpdatedAt]
  )
  // useEffect(() => {
  //   console.log('fx')
  //   const areAllAnswersEmpty = Object.values(fieldAnswers).every(
  //     answer => answer.length === 0 || answer?.includes('none')
  //   )
  //   console.log({ areAllAnswersEmpty })
  // if (areAllAnswersEmpty) {
  //   uniq(options)[0] === 'none' && updateChangeAll({ none: 'checked' }, {})
  // }
  // }, [])

  useEffect(() => {
    const allOptions = uniq(['none', ...options])
    const allAnswers = Object.values(fieldAnswers)
    const newCombinedAnswer: Record<string, 'unchecked' | 'checked' | 'indeterminate'> = {}
    allOptions.forEach(option => {
      const matchingAnswers = allAnswers?.filter(answer => answer?.includes(option))
      if (!matchingAnswers?.length) {
        newCombinedAnswer[option] = 'unchecked'
      } else if (matchingAnswers?.length === programEngagementFields.data?.length) {
        newCombinedAnswer[option] = 'checked'
      } else {
        newCombinedAnswer[option] = 'indeterminate'
      }
    })
    setCombinedAnswer(newCombinedAnswer)
  }, [fieldAnswers, programEngagementFields.data])

  useEffect(() => {
    if (Object.keys(fieldAnswers).length === 0) {
      const initialFieldAnswers: Record<string, string[]> = {}
      programEngagementFields.data?.forEach(field => {
        initialFieldAnswers[field.resourceId] =
          (dataEntryFieldDetails
            ? dataEntryFieldDetails
                .find(dataEntryField => dataEntryField.resourceId === field.id)
                ?.details.find(detail => detail.traitId === traitId && detail.year === year)?.result
            : field.findDetail?.({ traitId, year })?.result) ?? []
      })
      setFieldAnswers(initialFieldAnswers)
    }
  }, [
    programEngagementFields.data,
    dataEntryFieldDetails,
    engagementDetails.dataUpdatedAt,
    detailRequirements?.[0]?.year,
  ])

  const onChangeAll = (event: SelectChangeEvent<string[]>) => {
    onUpdating?.()
    const prevValue = combinedAnswer

    // 'none' is an exclusive option.
    if ((event.target.value as string[])?.includes('none') && prevValue.none !== 'checked') {
      setCombinedAnswer({ none: 'checked' })

      updateChangeAll({ none: 'checked' }, prevValue)
    }
    // other options need to be considered individually
    else {
      setCombinedAnswer(prevValue => {
        const newCombinedAnswer: Record<string, string> = {}
        options.forEach(option => {
          if (option !== 'none') {
            if (prevValue[option] === 'indeterminate') {
              newCombinedAnswer[option] = !(event.target.value as string[]).includes(option)
                ? // if previous value is indetermine and value is not included, maintain existing value
                  'indeterminate'
                : 'checked'
            } else {
              newCombinedAnswer[option] = !(event.target.value as string[]).includes(option)
                ? // if previous value is checked, unchecked or undefined and value is not included, set to unchecked
                  'unchecked'
                : 'checked'
            }
          } else {
            newCombinedAnswer.none = 'unchecked'
          }
        })
        updateChangeAll(newCombinedAnswer, prevValue)

        return newCombinedAnswer
      })
    }
  }

  const updateChangeAll = useCallback(
    (passedCombinedAnswer: Record<string, string>, prevValue: Record<string, string>) => {
      let fieldUpdates: undefined | ResourceDetailsUpdate[] = []
      if (passedCombinedAnswer.none === 'checked') {
        fieldUpdates = programEngagementFields.data?.map(
          field =>
            ({
              resourceId: field.resourceId,
              details: [
                {
                  traitId,
                  year: detailRequirements?.[0]?.year,
                  result: ['none'],
                },
              ],
            } as ResourceDetailsUpdate)
        )
      } else {
        fieldUpdates = programEngagementFields.data?.map(field => {
          const existingFieldValue = dataEntryFieldDetails
            ? dataEntryFieldDetails
                .find(({ resourceId }) => resourceId === field.id)
                ?.details.find(detail => detail.traitId === traitId)?.result
            : (field.findDetail?.({ traitId, year })?.result as string[])
          const newFieldValue = options.map(option => {
            // if all values is indeterminate, maintain field value
            if (passedCombinedAnswer[option] === 'indeterminate') {
              return existingFieldValue?.find((value: string) => option === value)
                ? option
                : undefined
            }
            // if all values is checked, add the value
            if (passedCombinedAnswer[option] === 'checked') {
              return option
            }
            // if all values is unchecked or undefined, remove the value
            return undefined
          })
          return {
            resourceId: field.resourceId,
            details: [
              {
                traitId,
                year,
                result: !!newFieldValue.filter(Boolean).filter(a => a !== 'none').length
                  ? newFieldValue.filter(Boolean).filter(a => a !== 'none')
                  : undefined,
              },
            ],
          } as ResourceDetailsUpdate
        })
      }

      if (!!fieldUpdates) {
        const previousFieldAnswers = fieldAnswers
        const updatedFieldAnswers: Record<string, string[]> = {}
        fieldUpdates.forEach(fieldUpdate => {
          updatedFieldAnswers[fieldUpdate.resourceId] = fieldUpdate.details[0].result
        })
        setFieldAnswers(updatedFieldAnswers)
        handleChange(fieldUpdates)
          .then(() => {
            onSuccess?.()
            setDisableAll(false)
          })
          .catch(error => {
            onError?.(error)
            enqueueSnackbar(t('failedToSave'), {
              variant: 'error',
            })
            setCombinedAnswer(prevValue)
            setDisableAll(false)
            setFieldAnswers(previousFieldAnswers)
          })
      }
    },
    [
      programEngagementFields.data,
      detailRequirements,
      setDisableAll,
      dataEntryFieldDetails,
      fieldAnswers,
    ]
  )

  return (
    <TableContainer style={{ borderWidth: 0 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('field', { ns: '@cibo/landmanager/SelectRollupEditor' })}</TableCell>
            <TableCell>{t('tableHeader')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {programEngagementFields.data && programEngagementFields.data.length > 1 && (
            <TableRow key="all">
              <TableCell>
                <Box sx={{ marginLeft: 2, py: 2 }}>
                  <Typography sx={{ fontWeight: 'bold', my: 2 }}>
                    {t('answerForAllFields', { ns: '@cibo/landmanager/SelectRollupEditor' })}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <MultiselectChangeAllInput
                  disabled={options.length === 0 || allDisabled}
                  immutable={
                    !!programEngagementFields.data?.filter(
                      field =>
                        !!field.findDetail?.({
                          traitId,
                          year: detailRequirements?.[0]?.year,
                        })?.immutable
                    ).length
                  }
                  onChange={
                    !allDisabled ? onChangeAll : (event: SelectChangeEvent<string[]>) => null
                  }
                  options={options}
                  optionsList={optionsList}
                  t={t}
                  traitId={traitId}
                  value={combinedAnswer || {}}
                />
              </TableCell>
            </TableRow>
          )}
          {programEngagementFields.data?.map((field, index) => (
            <TableRow key={field.resourceId}>
              <TableCell>
                <Box sx={{ marginLeft: 2, py: 2 }}>
                  {/* @ts-ignore this isn't a data grid, so it's missing some props */}
                  <FieldNameCell row={field} />
                </Box>
              </TableCell>
              <TableCell>
                <MultiselectFieldInput
                  disabled={options.length === 0}
                  handleChange={handleChange}
                  onError={onError}
                  onSuccess={onSuccess}
                  onUpdating={onUpdating}
                  options={options}
                  optionsList={optionsList}
                  row={field}
                  setValue={(value: string[]) =>
                    setFieldAnswers(prev => ({ ...prev, [field.resourceId]: value }))
                  }
                  t={t}
                  traitId={traitId}
                  value={fieldAnswers[field.resourceId] || []}
                  year={year}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
